/**
 * Reports Page
 *
 * Provides access to financial and operational reports
 * organized by department with location-based filtering.
 */

import React, { useState, useMemo } from 'react';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { useLocationParam } from '../hooks/useLocationParam';
import { LocationDropdown, LOCATIONS } from '../components/LocationDropdown';
import { ReportDepartmentTabs, ReportList, ReportModal, WholesaleComparisonModal, AdExpenseComparisonModal, LeadPacingReportModal, TsmRankingReportModal, TsmGroupRankingReportModal, TsmItemsReportModal, TradeInReportModal, PartsCounterReportModal, PredictionScorecardModal, SalesTeamFunnelReportModal } from '../components/reports';
import { ReportDepartmentKey, ReportDefinition, ReportModalState } from '../types/reports';
import { useAuth } from '../auth/AuthContext';

export const Reports: React.FC = () => {
  const { assignedDepartmentIds, userRole } = useAuth();
  const { locationId, setLocationId } = useLocationParam();

  const hasDeptRestriction = assignedDepartmentIds && assignedDepartmentIds.length > 0;
  const defaultDepartment: ReportDepartmentKey = hasDeptRestriction
    ? (assignedDepartmentIds[0] as ReportDepartmentKey)
    : 'NEW_VEHICLE';

  const [selectedDepartment, setSelectedDepartment] = useState<ReportDepartmentKey>(defaultDepartment);
  const [modalState, setModalState] = useState<ReportModalState>({
    isOpen: false,
    report: null,
  });

  // Get location name for display
  const locationName = useMemo(() => {
    return LOCATIONS.find((loc) => loc.id === locationId)?.name || 'Unknown Location';
  }, [locationId]);

  // Handle report selection - opens modal
  const handleReportSelect = (report: ReportDefinition) => {
    setModalState({
      isOpen: true,
      report,
    });
  };

  // Close modal
  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      report: null,
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page content — hidden when printing (modal handles its own print layout) */}
      <div className="print:hidden">
        {/* Title Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <DocumentChartBarIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Darling's Reports</h1>
              <p className="text-gray-500">Generate and export financial reports</p>
            </div>
          </div>
        </div>

        {/* Control Section - Location Dropdown */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center space-x-4">
            <label htmlFor="location-select" className="text-sm font-medium text-gray-700">
              Location:
            </label>
            <LocationDropdown
              value={locationId}
              onChange={setLocationId}
            />
          </div>
        </div>

        {/* Department Tabs */}
        <ReportDepartmentTabs
          selectedDepartment={selectedDepartment}
          onDepartmentChange={setSelectedDepartment}
          assignedDepartmentIds={hasDeptRestriction ? assignedDepartmentIds : undefined}
          userRole={userRole}
        />

        {/* Report List */}
        <ReportList
          department={selectedDepartment}
          locationId={locationId}
          locationName={locationName}
          onReportSelect={handleReportSelect}
        />
      </div>

      {/* Report Modal */}
      {modalState.report && modalState.report.multiLocation && modalState.report.id === 'ad-expense-comparison' && (
        <AdExpenseComparisonModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && modalState.report.multiLocation && modalState.report.id === 'lead-pacing-report' && (
        <LeadPacingReportModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && modalState.report.multiLocation && modalState.report.id === 'tsm-ranking-report' && (
        <TsmRankingReportModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && modalState.report.multiLocation && modalState.report.id === 'tsm-group-ranking-report' && (
        <TsmGroupRankingReportModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && modalState.report.multiLocation && modalState.report.id === 'tsm-items-report' && (
        <TsmItemsReportModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && modalState.report.multiLocation && modalState.report.id === 'prediction-scorecard' && (
        <PredictionScorecardModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && modalState.report.multiLocation && modalState.report.id === 'sales-team-funnel-report' && (
        <SalesTeamFunnelReportModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && modalState.report.multiLocation && modalState.report.id !== 'ad-expense-comparison' && modalState.report.id !== 'lead-pacing-report' && modalState.report.id !== 'tsm-ranking-report' && modalState.report.id !== 'tsm-group-ranking-report' && modalState.report.id !== 'tsm-items-report' && modalState.report.id !== 'prediction-scorecard' && modalState.report.id !== 'sales-team-funnel-report' && (
        <WholesaleComparisonModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && modalState.report.id === 'parts-counter-performance-report' && (
        <PartsCounterReportModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
        />
      )}
      {modalState.report && !modalState.report.multiLocation && modalState.report.id === 'trade-in-report' && (
        <TradeInReportModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          locationId={locationId}
          locationName={locationName}
        />
      )}
      {modalState.report && !modalState.report.multiLocation && modalState.report.id !== 'trade-in-report' && modalState.report.id !== 'parts-counter-performance-report' && (
        <ReportModal
          report={modalState.report}
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          locationId={locationId}
          locationName={locationName}
          department={selectedDepartment}
        />
      )}
    </div>
  );
};
